/**
 * P5-04: 业务埋点系统
 * 事件队列 + beacon 上报
 */

export type EventType =
  | 'page_view'
  | 'book_open'
  | 'page_flip'
  | 'tts_play'
  | 'interaction_click'
  | 'ai_generate_start'
  | 'ai_generate_complete'
  | 'ai_generate_fail'
  | 'subscription_click'
  | 'payment_complete'
  | 'share_create'
  | 'favorite_toggle'
  | 'review_submit'
  | 'login'
  | 'logout'
  | 'reader_mode_switch';

interface TrackEvent {
  event_type: EventType;
  event_data: Record<string, any>;
  page_path: string;
  timestamp: number;
  session_id: string;
}

const SESSION_ID = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const REPORT_URL = import.meta.env.VITE_MONITOR_URL || '/api/events';
const FLUSH_INTERVAL = 5000; // 5s 批量上报
const MAX_QUEUE = 50;

let eventQueue: TrackEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flush() {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.slice(0, MAX_QUEUE);
  eventQueue = eventQueue.slice(MAX_QUEUE);

  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(REPORT_URL, JSON.stringify(batch));
    } else {
      fetch(REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // 静默失败
  }

  // 如果还有剩余事件，继续 flush
  if (eventQueue.length > 0) {
    setTimeout(flush, 100);
  }
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, FLUSH_INTERVAL);
}

/** 上报业务事件 */
export function track(eventType: EventType, eventData: Record<string, any> = {}) {
  const event: TrackEvent = {
    event_type: eventType,
    event_data: eventData,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    timestamp: Date.now(),
    session_id: SESSION_ID,
  };

  eventQueue.push(event);

  // 立即 flush 关键事件
  if (
    eventType === 'payment_complete' ||
    eventType === 'ai_generate_complete' ||
    eventType === 'ai_generate_fail'
  ) {
    flush();
  } else {
    scheduleFlush();
  }
}

/** 页面离开前强制 flush */
export function initTracker() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeunload', flush);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}

/** 获取当前会话 ID（用于关联分析） */
export function getSessionId(): string {
  return SESSION_ID;
}
